import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {TimelineLite, TweenLite} from "gsap";
import {ContinueButton} from "@nolimitcity/slot-launcher/bin/gui/buttons/concretebuttons/ContinueButton";
import {TheCryptSoundManager} from "../sound/TheCryptSoundManager";
import {TheCryptSoundConfig} from "../configs/TheCryptSoundConfig";
import {TheCrypt} from "../TheCrypt";
import {BasicBitmapCountUp} from "../components/BasicBitmapCountUp";
import {TheCryptGameAssets} from "../TheCryptGameAssets";
import {SpineTween} from "@nolimitcity/slot-game/bin/animation/SpineTween";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {BitmapText} from "pixi.js";
import {TheCryptHelper} from "../utils/TheCryptHelper";
import {TheCryptWinConfig} from "../configs/TheCryptWinConfig";

export class TheCryptFreespinOutro extends PIXI.Container implements StageLayer {
    private _spine: PIXI.spine.Spine | null;
    private _spineContainer: PIXI.Sprite;
    private _button: ContinueButton;
    private _spinsPlayedText: PIXI.Text;
    private _spinsPlayedNr: BitmapText;
    private _clickCallback: () => void;
    private _countUp: BasicBitmapCountUp;
    private _spinsPlayedTextStyle = {
        fontFamily: "Helvetica",
        align: "center",
        fill: "#47ff9e",
        fontSize: 40,
    };

    constructor() {
        super();
        this.name = "FreespinOutro";
        this.init();
    }

    private getFSWinType(): string {
        const win: number = TheCrypt.model.accumulatedRoundWin;
        if (win > TheCrypt.model.gameData.playedBetValue * 100) {
            return "good";
        } else if (win > TheCrypt.model.gameData.playedBetValue * 5) {
            return "normal";
        }
        return "bad";
    }

    private onButtonClick(playClickSound: boolean): void {
        if (playClickSound) {
            TheCryptSoundManager.playSound(TheCryptSoundConfig.CONTINUE_BUTTON);
        }
        const hideFSOuttroTL: TimelineLite = new TimelineLite();
        hideFSOuttroTL.add(() => {
            TheCrypt.keypadPlugin.hide();
            TheCrypt.collectors.hide();
            this._button.enable(false);
            this._clickCallback();
            this._clickCallback = () => {
            };
        });

        if (playClickSound) {
            hideFSOuttroTL.add([
                TheCrypt.gameViewContainer.removeDimmer(),
                () => {
                    TweenLite.to(this, 0.2, {alpha: 0})
                    TweenLite.to([this._button], 0.2, {alpha: 0})
                }
            ]);
        } else {
            hideFSOuttroTL.add(() => {
                this.alpha = 0;
                this._button.alpha = 0;
            });
        }

        hideFSOuttroTL.add(() => {
            this.visible = false;
            this._spineContainer.removeChildren();
        });
    }

    public onResize(): void {
        this._button.position.set(360, Math.max(530, NolimitApplication.screenBounds.bottom - 180));
    }

    private init(): void {
        this._spineContainer = new PIXI.Sprite();
        this.addChild(this._spineContainer);
        this._spineContainer.position.set(360, 360);
        this._button = new ContinueButton();
        this._button.addClickCallback(() => this.onButtonClick(true));
        this._button.position.set(360, 530);
        this._button.pivot.set(this._button.width * 0.5, 0);
        this._button.enable(false);
        this._button.alpha = 0;

        this._spinsPlayedText = new PIXI.Text("x", this._spinsPlayedTextStyle);
        this._spinsPlayedText.visible = false;
        this._spinsPlayedText.anchor.set(0.5);
        this._spinsPlayedText.position.set(360, 500);
        this.addChild(this._spinsPlayedText);

        this._spinsPlayedNr = new BitmapText("22", {
            fontName: TheCryptGameAssets.BITMAP_FONT_FS_SPINS,
            fontSize: 40
        });
        this._spinsPlayedNr.visible = false;
        this._spinsPlayedNr.letterSpacing = 10;
        this.addChild(this._spinsPlayedNr);

        this.addChild(this._button);

        this._countUp = new BasicBitmapCountUp({
            fontName: TheCryptGameAssets.BITMAP_FONT_WIN_COUNT_UP,
            fontSize: 50,
            tint: 0xffffff
        }, 520, new PIXI.Point(0.5, 0.5));
        this._countUp.position.set(360, 305);
        this._countUp.stopAt(0);
        this.visible = false;
        this.addChild(this._countUp);
        this.visible = false;
    }

    public show(callback: () => void, isReplay: boolean = false): TimelineLite {
        const tl: TimelineLite = new TimelineLite(),
            totalWin: number = TheCrypt.model.gameData.accumulatedRoundWin,
            isZeroWin: boolean = (totalWin === 0),
            startPoint: number = TheCrypt.model.hasNoAvalancheWin ? 2 : 0;
        if (this._spine) {
            this._spine.destroy();
            this._spine = null;
        }
        this._spine = new PIXI.spine.Spine(TheCrypt.assetLoader.getSpineAsset("FS_Outro"));
        this._spineContainer.addChild(this._spine);
        this._spine!.skeleton.setSkinByName(this.getFSWinType());
        this.onResize();
        tl.add(() => {
            this._clickCallback = callback;
        });
        //Add no win delay
        tl.add(() => {}, startPoint);
        this._spinsPlayedText.position.y = isZeroWin ? 360 : 500;
        this._countUp.visible = false;
        if (TheCrypt.model.gameData && TheCrypt.model.gameData.brokeTheBank) {
            this.onButtonClick(false);
        } else {
            const soundTimeline: TimelineLite = new TimelineLite().add(() => TheCryptSoundManager.playAmbientLoop(), 0.527);
            tl.add([
                soundTimeline,
                TheCrypt.gameViewContainer.addDimmer(true),
                new SpineTween(this._spine, 0, "start"),
                () => {
                    TheCrypt.keypadPlugin.hide();
                    this._spinsPlayedText.text = TheCrypt.apiPlugin.translations.translate(`Spins played`);
                    this._spinsPlayedNr.text = `${TheCrypt.model.gameData.numberOfFreespinsPlayed}`;
                    this._spinsPlayedNr.position.set(360 - this._spinsPlayedNr.width * 0.5, isZeroWin ? 289 : 429);
                    this.visible = true;
                    this.alpha = 1;
                    this._spinsPlayedText.visible = true;
                    this._spinsPlayedNr.visible = true;
                    TheCryptSoundManager.playSound(TheCryptSoundConfig.FREESPIN_OUTRO, 0);
                }],startPoint);

            tl.add([
                TweenLite.to(this._spinsPlayedNr, 0.1, {alpha: 1}),
                TweenLite.to(this._spinsPlayedText, 0.1, {alpha: 1}),
            ]);
            if (!isZeroWin) {
                const betLevel = TheCrypt.apiPlugin.freeBets.hasFreeBets() ? TheCrypt.apiPlugin.freeBets.getBet() : TheCrypt.apiPlugin.betLevel.getLevel(),
                    price: number = TheCrypt.model.gameData?.playedBetValue || parseFloat(betLevel),
                    smallWinPrice: number = TheCryptWinConfig.WIN_RATIOS.normalWin[1].ratio * price,
                    mediumWinPrice: number = TheCryptWinConfig.WIN_RATIOS.normalWin[2].ratio * price,
                    winType: string = (totalWin <= smallWinPrice ? (price === totalWin ? "Small" : "Tiny") : (totalWin === mediumWinPrice ? "Medium" : "Large"));
                const isWinBelowStake: boolean = TheCryptHelper.isWinBelowStake();
                const hasCountUp:boolean = !(isWinBelowStake || winType === "Tiny");
                tl.add(() => {
                    this._countUp.stopAt(0);
                    this._countUp.scale.set(0.5, 0.5);
                    this._countUp.alpha = 0;
                    this._countUp.visible = true;
                });

                tl.add([
                    ()=>{
                        if (hasCountUp) {
                            TheCryptSoundManager.playSound("winCountup");
                        }
                    },
                    TweenLite.to(this._countUp, 0.1, {alpha: 1}),
                    TweenLite.to(this._countUp.scale, 0.1, {x: 1, y: 1, ease: "sine.in"}),
                    this._countUp.getCountAnimation(0, totalWin, hasCountUp ? 1.5 : 0)
                ]);
                if (hasCountUp) {
                    tl.add(() => {
                        TheCryptSoundManager.stopSoundEffect("winCountup");
                        TheCryptSoundManager.playSound(`win${winType}`);
                    });
                }
            }

            tl.add([
                TweenLite.to(this._button, 0.4, {alpha: 1})
            ]);
            tl.add([
                () => {
                    this._button.enable(true);
                }]);
        }
        return tl;
    }
}